import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LibraryHeader } from '../components/PageComponents/header/LibraryHeader';
import { AlbumCard } from '../components/PageComponents/cards/AlbumCard';
import { Album } from '../components/PageComponents/albumList/album';
import { getTracksForAlbum, isTrackInAlbum } from '../components/PageComponents/albumList/getAllAlbums';
import { fetchPaged } from '../components/PageComponents/pagination/fetchPaged';
import { usePagedUrl, totalPageCount } from '../components/PageComponents/pagination/usePagedUrl';
import { Pagination } from '../components/PageComponents/pagination/Pagination';
import FilterBar, { readFilters, type FilterSpec, type FilterState } from '../components/PageComponents/filters/FilterBar';
import { API_BASE } from '../config/api';
import { PlayerContext } from '../context/PlayerContext';
import '../components/PageComponents/cards/Cards.css';

const ALBUM_FILTER_SPEC: FilterSpec = {
    defaultSort: 'id',
    defaultOrder: 'asc',
    sorts: [
        { value: 'id', label: 'Default' },
        { value: 'title', label: 'Title' },
        { value: 'release_date', label: 'Release date' },
        { value: 'artist', label: 'Artist' },
        { value: 'recent', label: 'Recently added' },
    ],
    selects: [
        {
            name: 'genre',
            label: 'All genres',
            value: '',
            options: [], // populated from /api/genre below
            multi: true, // checkbox dropdown, repeated genre= params
        },
        {
            name: 'primary_type',
            label: 'Any primary type',
            value: '',
            options: [
                { value: 'Album', label: 'Album' },
                { value: 'EP', label: 'EP' },
                { value: 'Single', label: 'Single' },
                { value: 'Compilation', label: 'Compilation' },
                { value: 'Soundtrack', label: 'Soundtrack' },
                { value: 'Live', label: 'Live' },
            ],
        },
        {
            name: 'secondary_type',
            label: 'Any secondary type',
            value: '',
            options: [
                { value: 'Studio', label: 'Studio' },
                { value: 'Live', label: 'Live' },
                { value: 'Compilation', label: 'Compilation' },
                { value: 'Dj-mix', label: 'DJ mix' },
                { value: 'Demo', label: 'Demo' },
                { value: 'Remix', label: 'Remix' },
            ],
        },
    ],
};

export default function Albums(): React.JSX.Element {
    const navigate = useNavigate();
    const { tracks, refreshTracks, setQueue, currentSong, isPlaying, togglePlay } = useContext(PlayerContext);
    const [albums, setAlbums] = useState<Album[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState<number | null>(null);
    const [storedPerPage, setStoredPerPage] = useState<number | undefined>(undefined);
    const { page, perPage, setPage, setPerPage } = usePagedUrl({ storedPerPage });

    // Filter/sort state, mirrored into the URL query string. Genre options
    // are loaded once from the genre table so the dropdown stays current.
    const [genreOptions, setGenreOptions] = useState<{ value: string; label: string }[]>([]);
    const [filterState, setFilterState] = useState<FilterState>(() => readFilters(ALBUM_FILTER_SPEC));
    const filterParams = useMemo(
        () => ({
            q: filterState.q,
            sort: filterState.sort,
            order: filterState.order,
            genre: filterState.multi.genre ?? [],
            primary_type: filterState.extra.primary_type ?? '',
            secondary_type: filterState.extra.secondary_type ?? '',
            year_from: filterState.extra.year_from ?? '',
            year_to: filterState.extra.year_to ?? '',
        }),
        [filterState],
    );

    useEffect(() => {
        let cancelled = false;
        fetch(`${API_BASE}/api/genre`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : []))
            .then((rows) => {
                if (cancelled || !Array.isArray(rows)) return;
                setGenreOptions(
                    rows.map((g: any) => ({ value: String(g.name ?? g), label: String(g.name ?? g) })),
                );
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, []);

    // The server-stored page size (ui.pagination.per_page) seeds the
    // default; without it the client default of 50 applies.
    useEffect(() => {
        let cancelled = false;
        fetch(`${API_BASE}/api/settings/ui`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : {}))
            .then((prefs: Record<string, string>) => {
                if (cancelled) return;
                const stored = Number.parseInt(prefs['ui.pagination.per_page'] ?? '', 10);
                if (!Number.isNaN(stored) && stored >= 1) setStoredPerPage(stored);
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        refreshTracks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const envelope = await fetchPaged<any>('/api/album', page, perPage, filterParams);
            if (cancelled) return;
            setAlbums(envelope.items.map((a: any) => Album.fromJson(a)));
            setTotal(envelope.total);
            const maxPage = totalPageCount(envelope.total, envelope.per_page);
            if (envelope.page > maxPage) {
                setPage(maxPage);
                return;
            }
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, filterParams]);

    const totalPages = totalPageCount(total ?? 0, perPage);
    const spec = useMemo(
        () => ({
            ...ALBUM_FILTER_SPEC,
            selects: ALBUM_FILTER_SPEC.selects.map((s) => (s.name === 'genre' ? { ...s, options: genreOptions } : s)),
        }),
        [genreOptions],
    );

    return (
        <>
            <div className="pageHeader">
                <LibraryHeader eyebrow="Your library" title="Albums" />
            </div>
            <div className="pageContent">
                <FilterBar spec={spec} state={filterState} onChange={setFilterState} resultCount={total ?? undefined} />
                {loading ? (
                    <div className="trackListEmpty">Loading albums…</div>
                ) : albums.length === 0 ? (
                    <div className="trackListEmpty">No albums in library</div>
                ) : (
                    <div className="cardGrid">
                        {albums.map((album) => {
                            const isThisAlbumCurrent = isTrackInAlbum(currentSong, album);
                            return (
                                <AlbumCard
                                    key={album.getId()}
                                    album={album}
                                    onClick={(a) => navigate(`/albums/${a.getId()}`)}
                                    isPlaying={isThisAlbumCurrent && isPlaying}
                                    onPlay={(a) => {
                                        if (isThisAlbumCurrent) togglePlay();
                                        else setQueue(getTracksForAlbum(tracks, a), 0);
                                    }}
                                />
                            );
                        })}
                    </div>
                )}
                {total !== null && totalPages > 1 && (
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        perPage={perPage}
                        onPageChange={setPage}
                        onPerPageChange={setPerPage}
                    />
                )}
            </div>
        </>
    );
}